import { useState } from "react";
import { captureError } from "@/lib/sentry";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { LANGUAGES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { WaveformIcon, ArrowLeftIcon, LockKeyIcon, SparkleIcon } from "@phosphor-icons/react";

export default function PressBoxCommentary() {
  const { user } = useAuth();
  const [topic, setTopic] = useState("");
  const [segment, setSegment] = useState("opening");
  const [style, setStyle] = useState("tv");
  const [language, setLanguage] = useState(user?.language_preference || "en");
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null);

  const isLocked = user?.plan !== "press_box_pro";

  const submit = async (e) => {
    e.preventDefault();
    if (!topic.trim() || isLocked) return;
    setLoading(true);
    setOutput(null);
    try {
      const res = await api.post("/press-box/commentary", { topic, segment, style, language });
      setOutput(res.data.text);
    } catch (err) {
      captureError("commentary generation failed:", err);
      setOutput("Generation failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (isLocked) {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto text-center py-20" data-testid="commentary-locked">
          <LockKeyIcon weight="fill" className="text-[#FDB927] mx-auto mb-3" size={36} />
          <h1 className="font-display font-bold text-3xl uppercase tracking-tighter mb-3">Press Box Pro feature</h1>
          <Link to="/pricing?plan=press_box_pro" data-testid="commentary-locked-cta" className="inline-flex items-center gap-1 px-5 py-2 rounded-md bg-[#FDB927] hover:bg-yellow-400 text-black text-xs font-bold uppercase tracking-wider">Upgrade</Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Link to="/press-box" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white mb-4" data-testid="back-to-press-box">
        <ArrowLeftIcon size={14} /> Back to Press Box
      </Link>
      <div className="mb-6 fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FDB927] mb-2 inline-flex items-center gap-1.5">
          <WaveformIcon weight="fill" /> Commentary Assistant
        </p>
        <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">On-Air Scripts</h1>
        <p className="text-white/60 text-sm mt-2">Opening segments, halftime hits, closing thoughts — TV or radio style, EN / ES / Bilingual.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="p-5 rounded-md bg-[#121212] border border-white/10 space-y-3" data-testid="commentary-form">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-2 block">Topic / matchup</label>
            <input
              data-testid="commentary-topic"
              required
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. Knicks vs Cavs, Game 7"
              className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-2 block">Segment</label>
              <select value={segment} onChange={(e) => setSegment(e.target.value)} data-testid="commentary-segment" className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm uppercase font-bold tracking-wider">
                <option value="opening">Opening</option>
                <option value="halftime">Halftime</option>
                <option value="closing">Closing</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-2 block">Style</label>
              <select value={style} onChange={(e) => setStyle(e.target.value)} data-testid="commentary-style" className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm uppercase font-bold tracking-wider">
                <option value="tv">TV</option>
                <option value="radio">Radio</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-2 block">Language</label>
              <select value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="commentary-lang" className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm uppercase font-bold tracking-wider">
                {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid="commentary-submit" className="w-full py-3 rounded-md bg-[#FDB927] hover:bg-yellow-400 text-black font-black uppercase tracking-wide inline-flex items-center justify-center gap-2">
            <SparkleIcon weight="fill" /> {loading ? "Generating…" : "Generate script"}
          </button>
        </form>

        <div className="p-5 rounded-md bg-[#121212] border border-white/10 min-h-[300px]">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FDB927] mb-3">On-Air Script</p>
          {output ? (
            <pre className="whitespace-pre-wrap font-sans text-sm text-white/90 leading-relaxed" data-testid="commentary-output">{output}</pre>
          ) : (
            <p className="text-white/50 text-sm">Your script will appear here.</p>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
